import dayjs from 'dayjs';
import { NoticeType } from '../../api/types/notice';
import { MonthlyNoticeMessageTemplateValueSet } from '../../types/notice';
import { useGenBottomSheetQueryParams } from '../../composables/useGenBottomSheetQueryParams';
import { MonthlyApplicationDetail } from '../../api/types/monthlyApplication';
import {
  MONTHLY_APPLICATION_COLUMNS,
  MONTHLY_APPLICATION_STATUS,
} from '../monthlyApplications';
const { separateByComma } = useNumConverter();
const { genBottomSheetQueryParams } = useGenBottomSheetQueryParams();

export const MONTHLY_NOTICE_TYPE = {
  PROVISIONAL_APPLICATION_REJECTED: 'PROVISIONAL_APPLICATION_REJECTED',
  PROVISIONAL_APPLICATION_RE_REVIEW: 'PROVISIONAL_APPLICATION_RE_REVIEW',
  PROVISIONAL_APPLICATION_RESUBMISSION_REQUEST:
    'PROVISIONAL_APPLICATION_RESUBMISSION_REQUEST',
  APPLICATION_REQUEST: 'APPLICATION_REQUEST',
  APPLICATION_REJECTED: 'APPLICATION_REJECTED',
  MONTHLY_PAYMENT_COMPLETED: 'MONTHLY_PAYMENT_COMPLETED',
  RENEWAL_REQUEST: 'RENEWAL_REQUEST',
  CONTRACT_SIGNATURE_COMPLETED: 'CONTRACT_SIGNATURE_COMPLETED',
  APPLICATION_RESUBMISSION_REQUEST: 'APPLICATION_RESUBMISSION_REQUEST',
  INITIAL_PAYMENT_REQUEST: 'INITIAL_PAYMENT_REQUEST',
  UNPAID_PAYMENT_COMPLETED: 'UNPAID_PAYMENT_COMPLETED',
  CONTRACT_SIGNATURE_REQUEST: 'CONTRACT_SIGNATURE_REQUEST',
  UNPAID_PAYMENT_DEADLINE_REQUEST: 'UNPAID_PAYMENT_DEADLINE_REQUEST',
  UNPAID_PAYMENT_RE_REQUEST: 'UNPAID_PAYMENT_RE_REQUEST',
  UPDATE_APPLICATION_RESUBMISSION_REQUEST:
    'UPDATE_APPLICATION_RESUBMISSION_REQUEST',
  UPDATE_REQUEST_REJECTED: 'UPDATE_REQUEST_REJECTED',
  UPDATE_REQUEST_REVIEW_APPROVED: 'UPDATE_REQUEST_REVIEW_APPROVED',
  UNPAID_PAYMENT_REQUEST: 'UNPAID_PAYMENT_REQUEST',
  CANCELLATION_REQUEST_APPROVED: 'CANCELLATION_REQUEST_APPROVED',
  CONTRACT_CANCELLATION_SCHEDULED: 'CONTRACT_CANCELLATION_SCHEDULED',
  CONTRACT_GARAGE_NUMBER_CHANGED: 'CONTRACT_GARAGE_NUMBER_CHANGED',
  CONTRACT_MONTHLY_AMOUNT_CHANGED: 'CONTRACT_MONTHLY_AMOUNT_CHANGED',
  UNSUBMITTED_IMAGE_SUBMISSION_REQUEST: 'UNSUBMITTED_IMAGE_SUBMISSION_REQUEST',
  CONTRACT_CANCELLATION_COMPLETED: 'CONTRACT_CANCELLATION_COMPLETED',
  RENEWAL_DUE_DATE_APPROACHING: 'RENEWAL_DUE_DATE_APPROACHING',
  CONTRACT_SIGNATURE_DUE_DATE_EXCEEDED: 'CONTRACT_SIGNATURE_DUE_DATE_EXCEEDED',
  APPLICATION_EXPIRED_CANCELLATION: 'APPLICATION_EXPIRED_CANCELLATION',
  INITIAL_PAYMENT_EXPIRED_CANCELLATION: 'INITIAL_PAYMENT_EXPIRED_CANCELLATION',
  RESUBMISSION_EXPIRED_CANCELLATION: 'RESUBMISSION_EXPIRED_CANCELLATION',
  UPDATE_RESUBMISSION_EXPIRED_REJECTION:
    'UPDATE_RESUBMISSION_EXPIRED_REJECTION',
  PROVISIONAL_APPLICATION_RE_REVIEW_BY_EXCLUSION:
    'PROVISIONAL_APPLICATION_RE_REVIEW_BY_EXCLUSION',
  CONTRACT_DOCUMENT_WITHDRAWAL: 'CONTRACT_DOCUMENT_WITHDRAWAL',
} as const;

export const MONTHLY_NOTICE_MESSAGES = [
  {
    TYPE: MONTHLY_NOTICE_TYPE.PROVISIONAL_APPLICATION_REJECTED,
    TITLE: '【月極駐車場契約】審査結果のお知らせ',
    CONTENTS: () => [
      'このたびはお申込いただきありがとうございました。今回のお申込について検討をさせていただいた結果、ご契約をお見送りさせていただくことになりました。',
      'ご意向に添えず大変恐縮ではございますが、何卒ご了承くださいますようお願い申し上げます。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.PROVISIONAL_APPLICATION_RE_REVIEW,
    TITLE: '【月極駐車場契約】仮申込内容再審査のご連絡',
    CONTENTS: ({ parkingName }) => [
      '先日、お申込み内容を審査の上で承認させていただきましたが、諸般の事情により、承認を一時取り下げ、再審査を行うこととなりました。',
      `・貸出申込み中駐車場：${parkingName}`,
      '現在、改めてお申込み内容の確認を進めております。審査が完了次第、改めて結果をご連絡いたしますので、今しばらくお待ちいただきますようお願い申し上げます。',
      'なお、今回の再審査に関しまして、ご不明な点や追加のご質問がございましたら、お気軽にお問い合わせください。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.PROVISIONAL_APPLICATION_RESUBMISSION_REQUEST,
    TITLE: '【月極駐車場契約】仮申込情報 再提出のご依頼',
    CONTENTS: ({ parkingName }) => [
      '仮申込の際にご提出をいただいた内容に不備がございましたので、再提出をお願いいたします。',
      '再提出ボタンを押下いただき、詳細についてご確認の上、お手続きをお願いいたします。',
      `・貸出申込み中駐車場：${parkingName}`,
    ],
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        applicationDetail.status ===
        MONTHLY_APPLICATION_STATUS
          .PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED.ID,
      LABEL: '再提出画面に進む',
      TO: '/monthly/application/:applicationId',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.APPLICATION_REQUEST,
    TITLE: '【月極駐車場契約】本申込情報のご提出の依頼',
    CONTENTS: ({ parkingName }) => [
      'このたび、お申し込みいただいた下記の月極駐車場の仮申込審査が完了いたしました。',
      'つきましては、契約手続きに進むため、本申込に必要な情報のご提出をお願いいたします。',
      `・貸出申込み中駐車場：${parkingName}`,
      'ご提出を確認次第、正式な契約手続きを進めさせていただきます。',
    ],
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        applicationDetail.status ===
        MONTHLY_APPLICATION_STATUS.APPLICATION_PENDING.ID,
      LABEL: '本申込に進む',
      TO: '/monthly/application/:applicationId',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.APPLICATION_REJECTED,
    TITLE: '【月極駐車場契約】本申込審査結果のご連絡',
    CONTENTS: ({ parkingName }) => [
      'このたびは、月極駐車場の本申込みをいただき、誠にありがとうございました。\n慎重に審査を行いました結果、誠に残念ながら今回のご契約を見送らせていただくこととなりました。',
      `・貸出申込み中駐車場：${parkingName}`,
      'ご希望に添えず申し訳ございませんが、何卒ご理解のほどお願い申し上げます。\nまた、別の駐車場をご希望の際は、改めてご相談いただければ幸いです。',
      '何かご不明点がございましたら、お気軽にお問い合わせください。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.APPLICATION_RESUBMISSION_REQUEST,
    TITLE: '【月極駐車場契約】本申込情報 再提出のご依頼',
    CONTENTS: ({ parkingName }) => [
      '本申込の際にご提出をいただいた内容に不備がございましたので、再提出をお願いいたします。',
      '再提出ボタンを押下いただき、詳細についてご確認の上、お手続きをお願いいたします。',
      `・貸出申込み中駐車場：${parkingName}`,
    ],
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        applicationDetail.status ===
        MONTHLY_APPLICATION_STATUS.APPLICATION_RESUBMISSION_REQUESTED.ID,
      LABEL: '再提出画面に進む',
      TO: '/monthly/application/:applicationId',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.INITIAL_PAYMENT_REQUEST,
    TITLE: '【月極駐車場契約】お支払い手続き前のご確認',
    CONTENTS: () => [
      '申込時にご登録のメールアドレス宛に、契約書のPDFデータをお送りしております。',
      'お支払い手続き前に、契約書をご一読の上、お支払い手続きいただきますようお願いいたします。',
      '初期費用お支払後のキャンセルはお受けできません。\n通常の解約手続きを行っていただきます。',
    ],
    FOOTER_BUTTON: {
      condition: ({ isCheckedNotice, applicationDetail }) =>
        !!isCheckedNotice && !!applicationDetail.initialPayment?.canPay,
      LABEL: 'お支払いへ進む',
      TO: genBottomSheetQueryParams('', 'MONTHLY_APPLICATION_INITIAL_PAYMENT'),
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_SIGNATURE_REQUEST,
    TITLE: '【月極駐車場契約】電子契約書署名のお願い',
    CONTENTS: () => [
      '先日お申込みいただきました弊社月極駐車場の、電子契約書へのご署名が未完了のため、ご連絡させていただきました。',
      'ご契約金はすでにお支払いが完了しておりますので、電子契約書へ署名することで契約成立となります。\nすでに契約期間が開始しておりますので、速やかにご署名をお願いいたします。',
      'なお、ご署名が完了しないまま一定期間が経過すると、契約放棄とみなし、ご契約は自動解除されます。\nその場合のお支払い済の契約金については、返金されませんのでご注意ください。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_SIGNATURE_COMPLETED,
    TITLE: '【月極駐車場契約】ご契約成立のご案内',
    CONTENTS: () => [
      'お申込みいただきました弊社月極駐車場のご契約が成立しましたことをお知らせいたします。',
      '契約書は、マイページの契約一覧よりご確認いただけます。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_SIGNATURE_DUE_DATE_EXCEEDED,
    TITLE: '【月極駐車場契約】（重要）契約自動破棄のお知らせ',
    CONTENTS: () => [
      '先日お申込みをいただいた月極駐車場における電子契約書へのご署名が未完了となっており、\n月額賃料お支払い処理が開始できませんでした。',
      '初期費用にてお支払い済み賃料の対象月を経過したため、契約が自動破棄となりました。',
      '再度ご契約をご希望の方は、仮申込みから改めてお手続きをお願いいたします。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UPDATE_APPLICATION_RESUBMISSION_REQUEST,
    TITLE: '【月極駐車場契約】更新申請情報の再提出のご依頼',
    CONTENTS: ({ parkingName }) => [
      '下記の月極駐車場の申込みに伴い、お申込情報に不備がございましたので、情報の再提出をお願いいたします。',
      `・貸出申込み中駐車場：${parkingName}`,
    ],
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        applicationDetail.status ===
        MONTHLY_APPLICATION_STATUS.CONTRACT_UPDATE_RESUBMISSION_REQUESTED.ID,
      LABEL: '再提出画面に進む',
      TO: '/monthly/:applicationId',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UPDATE_REQUEST_REJECTED,
    TITLE: '【月極駐車場契約】更新申請 審査結果のご連絡',
    CONTENTS: ({ parkingName }) => [
      'このたびは、申込者情報の更新申請をいただき、誠にありがとうございます。\n申請内容を確認させていただきましたが、誠に申し訳ございませんが、今回の更新申請は承認いたしかねる形となります。',
      `・貸出申込み中駐車場：${parkingName}`,
      '大変恐縮ですが、現在の登録情報のままでのお手続きとなりますことをご了承くださいますようお願いいたします。\nもし、追加で対応可能な方法がございましたら、別途ご案内させていただきますので、ご不明点がございましたらお気軽にお問い合わせください。',
      '引き続き、よろしくお願い申し上げます。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UPDATE_REQUEST_REVIEW_APPROVED,
    TITLE: '【月極駐車場契約】更新申請 審査結果のご連絡',
    CONTENTS: ({ parkingName }) => [
      'このたびは、申込者情報の更新申請をいただき、誠にありがとうございます。\n申請内容を確認し、更新を承認いたしましたのでお知らせいたします。',
      `・貸出申込み中駐車場：${parkingName}`,
      'ご登録情報が新しい内容に更新されております。\n万が一、申請内容と相違がある場合やご不明点がございましたら、お気軽にお問い合わせください。',
      '引き続き、よろしくお願い申し上げます。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.MONTHLY_PAYMENT_COMPLETED,
    TITLE: '【月極駐車場契】月額賃料クレジット決済完了のお知らせ',
    CONTENTS: ({
      parkingName,
      garageNumber,
      paymentCompleteAt,
      paymentTargetMonth,
      contractMonthlyAmount,
      billingCardNumber,
    }) => {
      if (!contractMonthlyAmount || !paymentTargetMonth) return [];
      const currentYear = new Date().getFullYear();
      return [
        `${currentYear}年${paymentTargetMonth}月分のクレジットカード決済が完了しましたのでお知らせいたします。`,
        `ご利用内容
------------------------------------------------------------
駐車場名：${parkingName}
区画番号：${garageNumber}番
決済完了日：${dayjs(paymentCompleteAt).format('YYYY/M/D')}
お支払賃料対象月：${currentYear}年${paymentTargetMonth}月分
クレジット決済完了金額：¥${separateByComma(contractMonthlyAmount)}円（税込）
------------------------------------------------------------
クレジットカード情報
------------------------------------------------------------
カード番号：${billingCardNumber}
------------------------------------------------------------`,
      ];
    },
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UNPAID_PAYMENT_REQUEST,
    TITLE: '【月極駐車場契約】月額賃料クレジット決済失敗のお知らせ',
    CONTENTS: ({
      parkingName,
      garageNumber,
      contractMonthlyAmount,
      paymentTargetMonth,
      paymentDueDate,
    }) => {
      if (!contractMonthlyAmount || !paymentTargetMonth) return [];
      const currentYear = new Date().getFullYear();
      return [
        'ご利用中の月極駐車場にて月額賃料のお支払いが未完了となっております。\n以下の内容をご確認いただき、指定の期日までにお手続きをお願いいたします。',
        `・駐車場名： ${parkingName}
・区画番号：${garageNumber}番
・未払賃料：¥${separateByComma(contractMonthlyAmount)}円（税込）
・お支払賃料対象月：${currentYear}年${paymentTargetMonth}月分
・お支払期限：${dayjs(paymentDueDate).format('YYYY年MM月DD日')}まで`,
      ];
    },
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        !!applicationDetail.unpaidPayment?.canPay,
      LABEL: '同意してお支払い画面に進む',
      TO: genBottomSheetQueryParams('', 'MONTHLY_BILLING_PAYMENT'),
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UNPAID_PAYMENT_RE_REQUEST,
    TITLE: '【月極駐車場契約】賃料未払いのお知らせ',
    CONTENTS: ({
      parkingName,
      garageNumber,
      contractMonthlyAmount,
      paymentTargetMonth,
      paymentDueDate,
    }) => {
      if (!contractMonthlyAmount || !paymentTargetMonth) return [];
      const currentYear = new Date().getFullYear();
      return [
        'ご契約中の月極駐車場の賃料が未払いとなっております。\n下記内容をご確認の上、お支払い期日までにお手続きをお願いいたします。',
        `・駐車場名：${parkingName}
・区画番号：${garageNumber}番
・未払賃料：¥${separateByComma(contractMonthlyAmount)}円（税込）
・お支払賃料対象月：${currentYear}年${paymentTargetMonth}月分
・お支払期限：${dayjs(paymentDueDate).format('YYYY年MM月DD日')}まで`,
        '下記お支払ボタンより、未払賃料のお支払手続きをお願いいたします。',
      ];
    },
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        !!applicationDetail.unpaidPayment?.canPay,
      LABEL: '同意してお支払い画面に進む',
      TO: genBottomSheetQueryParams('', 'MONTHLY_BILLING_PAYMENT'),
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UNPAID_PAYMENT_DEADLINE_REQUEST,
    TITLE: '【月極駐車場契約】※賃料未払いのお知らせ《本日最終締切》',
    CONTENTS: ({
      parkingName,
      garageNumber,
      contractMonthlyAmount,
      paymentTargetMonth,
      paymentDueDate,
    }) => {
      const currentYear = new Date().getFullYear();
      if (!contractMonthlyAmount || !paymentTargetMonth) return [];
      return [
        'ご契約中の月極駐車場の未払賃料について、延長支払い期限が本日までとなっております。\n下記内容をご確認の上、至急お手続きをお願いいたします。',
        `・駐車場名：${parkingName}
・区画番号：${garageNumber}番
・未払賃料：¥${separateByComma(contractMonthlyAmount)}円（税込）
・お支払賃料対象月：${currentYear}年${paymentTargetMonth}月分
・お支払期限：${dayjs(paymentDueDate).format('YYYY年MM月DD日')}まで`,
        '下記お支払ボタンより、未払賃料のお支払手続きをお願いいたします。',
      ];
    },
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        !!applicationDetail.unpaidPayment?.canPay,
      LABEL: '同意してお支払い画面に進む',
      TO: genBottomSheetQueryParams('', 'MONTHLY_BILLING_PAYMENT'),
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UNPAID_PAYMENT_COMPLETED,
    TITLE: '【月極駐車場契約】未払賃料支払い完了お知らせ',
    CONTENTS: ({
      parkingName,
      garageNumber,
      contractMonthlyAmount,
      paymentTargetMonth,
    }) => {
      if (!contractMonthlyAmount || !paymentTargetMonth) return [];
      const currentYear = new Date().getFullYear();
      return [
        'ご契約中の月極駐車場における未払賃料のお支払いが完了いたしました。',
        `・駐車場名：${parkingName}
・区画番号：${garageNumber}番
・お支払金額：¥${separateByComma(contractMonthlyAmount)}円（税込）
・お支払賃料対象月：${currentYear}年${paymentTargetMonth}月分`,
      ];
    },
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.RENEWAL_REQUEST,
    TITLE: '【月極駐車場契約】新条件のご確認と更新手数料お支払いのお願い',
    CONTENTS: ({
      parkingName,
      garageNumber,
      contractRenewalAmount,
      contractRenewalAmountWithTax,
      contractMonthlyAmount,
      paymentDueDate,
      contractEndDate,
    }) => {
      if (!contractRenewalAmount || !contractMonthlyAmount) return [];
      return [
        'ご契約中の月極駐車場の契約期間満了に伴い、契約条件のご確認と更新手数料のお支払いをお願い申し上げます。\n以下の内容をご確認いただき、指定の期日までにお手続きをお願いいたします。',
        `・駐車場名：${parkingName}
・区画：${garageNumber}番
・現契約満了日：${dayjs(contractEndDate).format('YYYY/MM/DD')}
・更新手数料：${separateByComma(contractRenewalAmount)}円（税込：${separateByComma(contractRenewalAmountWithTax)}）
・お支払い期限：${dayjs(paymentDueDate).format('YYYY/MM/DD')}まで`,
      ];
    },
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        applicationDetail.status ===
          MONTHLY_APPLICATION_STATUS.CONTRACT_SCHEDULED_CANCELLATION.ID &&
        !!applicationDetail.renewalPayment?.canPay,
      LABEL: '同意して契約内容の確認画面に進む',
      TO: '/monthly/renewalcontract/:applicationId',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.RENEWAL_DUE_DATE_APPROACHING,
    TITLE: '【月極駐車場契約】更新手続き未完了のお知らせ',
    CONTENTS: () => [
      'ご契約中の月極駐車場の更新手続き期日まで、残り１週間となっております。',
      '契約期間満了日にて使用を終了する場合は、解約のお手続きをお願いいたします。\n解約は、解約お申込日の翌月末日が最短解約日となりますのでご注意ください。',
    ],
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_GARAGE_NUMBER_CHANGED,
    TITLE: '【月極駐車場契約】契約中区画の変更について',
    CONTENTS: ({
      parkingName,
      garageNumber,
      oldGarageNumber,
      contractMonthlyAmount,
      contractUpdatedDate,
    }) => [
      'このたび、お客様がご契約中の駐車区画につきまして、貸出区画の変更が発生しましたのでご案内いたします。',
      `・駐車場名：${parkingName}
・新しい契約区画番号：${garageNumber}番（旧：${oldGarageNumber}番）
・月額料金：¥${separateByComma(contractMonthlyAmount)}円）
・変更適用日：${dayjs(contractUpdatedDate).format('YYYY年MM月DD日')}`,
      '変更に関しましてご不明な点がございましたら、お気軽にお問い合わせください。',
      '引き続き、どうぞよろしくお願いいたします。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_MONTHLY_AMOUNT_CHANGED,
    TITLE: '【月極駐車場契約】契約中区画の月額料金の変更について',
    CONTENTS: ({
      parkingName,
      garageNumber,
      contractMonthlyAmount,
      previousMonthlyAmount,
      contractUpdatedDate,
    }) => [
      'このたび、お客様がご契約中の駐車区画につきまして、月額料金の変更が発生しましたのでご案内いたします。',
      `・駐車場名：${parkingName}
・区画番号：${garageNumber}番
・新しい月額料金：¥${separateByComma(contractMonthlyAmount)}（旧：¥${previousMonthlyAmount}）
・変更適用日：${dayjs(contractUpdatedDate).format('YYYY年MM月DD日')}`,
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CANCELLATION_REQUEST_APPROVED,
    TITLE: '【月極駐車場契約】解約日決定のお知らせ',
    CONTENTS: ({
      parkingName,
      garageNumber,
      requestedCloseDate,
      contractEndDate,
    }) => [
      'お客様の解約申込みが無事に受付され、解約日が下記の通り決定いたしましたのでお知らせいたします。以下の内容をご確認ください。',
      `・駐車場名：${parkingName}
・区画番号：${garageNumber}番
・解約申込日：${dayjs(requestedCloseDate).format('YYYY年MM月DD日')}
・契約終了日：${dayjs(contractEndDate).format('YYYY年MM月DD日')}`,
      '今後、何かご不明な点やご質問がございましたら、どうぞお気軽にご連絡ください。\nまた、再度のご利用を心よりお待ちしております。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_CANCELLATION_SCHEDULED,
    TITLE: '【月極駐車場契約】解約予定のお知らせ',
    CONTENTS: ({ parkingName, contractCloseDate }) => {
      return [
        '平素より当駐車場をご利用いただき、誠にありがとうございます。\n大変恐縮ではございますが、ご契約の下記月極駐車場につきましては、施設の閉鎖に伴い、ご契約を解約させていただくこととなりました。',
        `・駐車場名：${parkingName}
・解約予定日：${dayjs(contractCloseDate).format('YYYY年MM月DD日')}
・利用終了日：${dayjs(contractCloseDate).format('YYYY年MM月DD日')} `,
        `解約日以降（${dayjs(contractCloseDate).format('YYYY年MM月DD日')}以降）は駐車場をご利用いただけなくなりますので、何卒ご了承ください。\n長らくのご利用に心より感謝申し上げますとともに、ご不便をおかけしますことをお詫び申し上げます。`,
      ];
    },
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_CANCELLATION_COMPLETED,
    TITLE: '【月極駐車場契約】解約完了のお知らせ',
    CONTENTS: ({ parkingName, closeDate }) => [
      '平素より当駐車場をご利用いただき、誠にありがとうございます。\n本日をもちまして契約期間が満了し、解約手続きが完了いたしましたことをご報告申し上げます。',
      `・駐車場名：${parkingName}
・解約日：${dayjs(closeDate).format('YYYY年MM月DD日')}`,
      '長らくのご利用に心より感謝申し上げますとともに、ご不便をおかけしますことをお詫び申し上げます。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.APPLICATION_EXPIRED_CANCELLATION,
    TITLE:
      '【月極駐車場契約】本申込手続きの期限切れによる申込キャンセルのお知らせ',
    CONTENTS: ({ parkingName }) => [
      'ご案内しておりました本申込手続きについて、有効期限を過ぎても手続きが完了しなかったため、お申込はキャンセル扱いとなりました。',
      `・貸出申込中駐車場：${parkingName}`,
      '恐れ入りますが、引き続きご利用を希望される場合は、仮申込から改めてお手続きをお願いいたします。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.INITIAL_PAYMENT_EXPIRED_CANCELLATION,
    TITLE:
      '【月極駐車場契約】初期費用の決済期限切れによる申込キャンセルのご案内',
    CONTENTS: ({ parkingName }) => [
      'ご案内していた初期費用の決済について、期限内に確認が取れなかったため、お申込はキャンセル扱いとさせていただきました。',
      `・貸出申込中駐車場：${parkingName}`,
      '恐れ入りますが、引き続きご利用を希望される場合は、仮申込から改めてお手続きをお願いいたします。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.RESUBMISSION_EXPIRED_CANCELLATION,
    TITLE: '【月極駐車場契約】再提出期限超過による申込キャンセルのご案内',
    CONTENTS: ({ parkingName }) => [
      '再提出をお願いしていた申込手続きについて、所定の期限までにご対応を確認できなかったため、該当申込はキャンセル扱いとなりました。',
      `・貸出申込中駐車場：${parkingName}`,
      '恐れ入りますが、引き続きご利用を希望される場合は、仮申込から改めてお手続きをお願いいたします。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UPDATE_RESUBMISSION_EXPIRED_REJECTION,
    TITLE: '【月極駐車場契約】再提出期限超過による契約情報更新申請却下のご案内',
    CONTENTS: ({ parkingName }) => [
      'ご案内していた契約情報更新申請の再提出について、所定の期限までにご対応を確認できなかったため、当該申請は却下とさせていただきました。',
      `・貸出申込中駐車場：${parkingName}`,
      '引き続き更新をご希望される場合は、新たに申請手続きをお願いいたします。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.UNSUBMITTED_IMAGE_SUBMISSION_REQUEST,
    TITLE: '【月極駐車場契約】ご契約成立のご案内',
    CONTENTS: ({ unsubmittedImageNames }) => {
      if (!unsubmittedImageNames || unsubmittedImageNames.length === 0)
        return [];
      const unsubmittedImages = unsubmittedImageNames.map(
        (name, index) => ` ${String.fromCharCode(9312 + index)} ${name}`,
      );
      return [
        'お申込みいただきました弊社月極駐車場のご契約が成立しましたことをお知らせいたします。',
        '契約書は、マイページの契約一覧よりご確認いただけます。',
        'また、お申込中の契約について、下記写真が未提出です。\n揃い次第アップロードをお願いします。',
        `${unsubmittedImages.join('\n')}`,
      ];
    },
    FOOTER_BUTTON: {
      condition: ({ applicationDetail }) =>
        applicationDetail.status ===
          MONTHLY_APPLICATION_STATUS
            .PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED.ID ||
        applicationDetail.status ===
          MONTHLY_APPLICATION_STATUS.APPLICATION_RESUBMISSION_REQUESTED.ID,
      LABEL: '写真の提出をする',
      TO: '/monthly/application/:applicationId',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.PROVISIONAL_APPLICATION_RE_REVIEW_BY_EXCLUSION,
    TITLE: '【月極駐車場契約】現在、本申込に進むことができません',
    CONTENTS: ({ parkingName }) => [
      '申込手続きの状況により、現在この申込では本申込に進むことができません。',
      `・貸出申込み中駐車場：${parkingName}`,
      '準備が整い次第、あらためて本申込のご案内を差し上げますので、今しばらくお待ちください。',
    ],
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
  {
    TYPE: MONTHLY_NOTICE_TYPE.CONTRACT_DOCUMENT_WITHDRAWAL,
    TITLE: '【月極駐車場契約】電子契約書の再送付について',
    CONTENTS: () => {
      return [
        '当社都合により、先日お送りした電子契約書の閲覧を一時的に停止しております。',
        '新しい契約書をメールにてお送りしますので、送信までしばらくお待ちくださいませ。',
      ];
    },
    FOOTER_BUTTON: {
      condition: () => true,
      LABEL: 'ホームへ戻る',
      TO: '/',
    },
  },
] satisfies {
  TYPE: NoticeType;
  TITLE: string;
  CONTENTS: (
    templateValueSet: Partial<MonthlyNoticeMessageTemplateValueSet>,
  ) => string[];
  FOOTER_BUTTON?: {
    condition?: ({
      applicationDetail,
    }: {
      applicationDetail: MonthlyApplicationDetail;
      isCheckedNotice?: boolean;
    }) => boolean;
    LABEL: string;
    TO: string;
  };
}[];
